import { IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Colors from '../../../utils/color';
import { ModGroupRowModel } from '../util/modGroupRow.utils';
import TruncationTooltip from './TruncationTooltip';

const useStyles = makeStyles<{ isUnmet: boolean; isOpen: boolean }>()(
  (_theme, { isUnmet, isOpen }) => ({
    row: {
      display: 'flex',
      alignItems: 'baseline',
      // 4px, matching `ModOptionRow`. At 8px the caret sat visibly adrift from
      // the name it belongs to, because the glyph carries its own side bearing
      // on top of the gap.
      gap: 4,
      width: '100%',
      minWidth: 0,
      // Vertical only. With every drawer open, this is what holds a group's
      // label off the previous group's last option — remove it and the pane
      // reads as one continuous list with no group boundaries at all.
      // Horizontally the row is flush: that padding bought nothing but lost
      // width on every line.
      padding: '3px 0',
      cursor: 'pointer',
    },
    // The key never truncates before the value does: it is sized to its own
    // content and refuses to shrink, so the value cell absorbs every loss.
    // Natural case, as `ModGroupLabel` always painted it. The uppercasing (and
    // the letter-spacing that propped it up) turned `drink_choice` into
    // `DRINK_CHOICE`, which is not the name anyone else in the console uses.
    // Prefix, name and counter truncate as ONE unit, so a long group label
    // ellipsises and its tooltip fires. At `flexShrink: 0` it could not shrink
    // at all: the label simply overran the row, and `TruncationTooltip` — which
    // tests `scrollWidth > clientWidth` — never saw a truncation to report.
    key: {
      flex: '0 1 auto',
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '12px',
      // 500, which is what MUI `subtitle2` resolves to — the variant
      // `ModGroupLabel` painted the name in, with no weight override and none
      // in the theme either. The bold this carried belonged to the original
      // 11px uppercase letter-spaced treatment, where it held a tiny all-caps
      // label together; with the caps gone it was weight doing no work, and it
      // flattened the contrast against the `[parent]` prefix, which legacy
      // deliberately DID set bold to set it apart from the name.
      fontWeight: 500,
      color: isUnmet ? Colors.Thunderbird : Colors.silverChalice,
    },
    // The `[parent]` prefix `ModGroupLabel` prints, so a revealed sub-group
    // still says which option revealed it.
    parentPrefix: {
      fontWeight: 700,
      color: Colors.silverChalice,
    },
    // The `(select …)` counter, beside the name where it has always been.
    subText: {
      fontSize: '11px',
      fontWeight: 400,
      color: isUnmet ? Colors.Thunderbird : Colors.silverChalice,
    },
    info: {
      flexShrink: 0,
      padding: 0,
      color: Colors.silverChalice,
      // The row is `alignItems: baseline`, and a button has no text baseline of
      // its own — the browser falls back to its bottom margin edge, which
      // floated the icon above the name. Centre this one child instead.
      alignSelf: 'center',
    },
    // Takes the remaining space and ellipsises. One line, always.
    //
    // The same string appears in the Agent Cart's modifier lines, so it is
    // typed the same way: `CartItemChildModGroup.itemNameClass` is a
    // `subtitle2` forced to 12px, which resolves to weight 500 / line-height
    // 1.57. Read side by side, one value in two places had no business being
    // two sizes.
    value: {
      flex: 1,
      minWidth: 0,
      textAlign: 'right',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: isUnmet ? Colors.Thunderbird : Colors.daintree,
    },
    // `flex-shrink: 0`, so the names lose characters and the count never lies
    // about how many there are.
    countChip: {
      flexShrink: 0,
      fontSize: '10px',
      fontWeight: 700,
      lineHeight: '14px',
      padding: '0 4px',
      borderRadius: 7,
      backgroundColor: Colors.alto,
      color: Colors.daintree,
    },
    // Identical to `ModOptionRow.furtherGroups`: same › glyph, same 12px, same
    // `silverChalice`, same 8px centred box. Both are disclosures saying "there
    // is more below this" — a triangle here and a chevron there made one idea
    // look like two, and the 8px box also lines the two gutters up down the
    // pane. Only the rotation is particular to this one, because only this one
    // has an open state.
    caret: {
      flexShrink: 0,
      width: 8,
      textAlign: 'center',
      fontSize: '12px',
      color: Colors.silverChalice,
      transform: isOpen ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease',
      display: 'inline-block',
    },
  })
);

interface ModGroupRowProps {
  model: ModGroupRowModel;
  isOpen: boolean;
  onToggle: () => void;
  /** Indent depth; 12px per level, matching `ModGroup.modGroupClass`. */
  depth?: number;
  /** The option that revealed this group, printed as `[parent]`. */
  parentModifier?: string;
  /** The group's spoken description, when it has one. */
  description?: string;
  /** Reads `description` out to the customer. Absent when there is none. */
  onInfoClick?: () => void;
}

export const MOD_GROUP_ROW_INDENT_PX = 12;

/**
 * HITL-1616 — one modifier group, one line.
 *
 * Replaces a wall of chips whose labels were vowel-stripped to fit
 * (`getModGroupItemLabel` → `novowelWithoutStitute`, which renders "Substitute
 * Sugar Free Vanilla" as `Sbsttt Sgr Fr Vnll`). The row shows the group's name
 * and what is actually chosen, at full spelling, and defers the options to the
 * drawer.
 *
 * The row is always rendered — it is the drawer below it that hides — because
 * the group's `ModGroup` must stay mounted for its default-selection effect.
 */
const ModGroupRow = ({
  model,
  isOpen,
  onToggle,
  depth = 0,
  parentModifier,
  description,
  onInfoClick,
}: ModGroupRowProps) => {
  const { classes } = useStyles({ isUnmet: model.isUnmet, isOpen });
  const { label, value, selectedNames, selectedCount, isUnmet, subText } =
    model;

  // What the tooltip says when the label is cut off: everything the row paints,
  // in the order it paints it, so the tooltip is never a partial answer.
  const fullLabel = [
    parentModifier ? `[${parentModifier}]` : '',
    isUnmet ? `! ${label}` : label,
    subText,
  ]
    .filter(Boolean)
    .join(' ');

  // The one place the full set is guaranteed readable, one name per line.
  const valueTooltip = selectedNames.length ? (
    <span style={{ whiteSpace: 'pre-line' }}>{selectedNames.join('\n')}</span>
  ) : (
    ''
  );

  return (
    <div
      className={classes.row}
      style={
        depth ? { paddingLeft: depth * MOD_GROUP_ROW_INDENT_PX } : undefined
      }
      onClick={onToggle}
      data-testid="mod-group-row"
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onToggle();
      }}
    >
      <span className={classes.caret}>›</span>
      <TruncationTooltip title={fullLabel}>
        <span className={classes.key} data-testid="mod-group-row-key">
          {parentModifier && (
            <span
              className={classes.parentPrefix}
            >{`[${parentModifier}] `}</span>
          )}
          {isUnmet ? `! ${label}` : label}
          {subText && <span className={classes.subText}>{` ${subText}`}</span>}
        </span>
      </TruncationTooltip>
      {onInfoClick && (
        <Tooltip title={description || ''} arrow placement="top">
          <IconButton
            size="small"
            className={classes.info}
            // The row toggles the drawer, so without this the icon would open
            // it as well as speak the description.
            onClick={(event) => {
              event.stopPropagation();
              onInfoClick();
            }}
            aria-label={`Play description for ${label}`}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      <TruncationTooltip title={valueTooltip}>
        <Typography
          component="span"
          variant="subtitle2"
          className={classes.value}
          data-testid="mod-group-row-value"
        >
          {value}
        </Typography>
      </TruncationTooltip>
      {selectedCount > 1 && (
        <span className={classes.countChip} data-testid="mod-group-row-count">
          ×{selectedCount}
        </span>
      )}
    </div>
  );
};

export default ModGroupRow;
